import React, {FC, useState, useEffect} from "react"
import {uniqBy} from "lodash"

import Flex from "@irati/ui/src/Flex"
import Button from "@irati/ui/src/Button"
import FormattedMessage, {getMessage, InjectIntl, injectIntl} from "@irati/utils/lang"
import {getErrorMessage, getErrorValues} from "@irati/utils/lang/formatError"
import {H2, Body} from "@irati/ui/src/Typography"
import Validation from "@irati/utils/validation"
import teamService from "@irati/identity/services/teams"
import ErrorText from "@irati/ui/src/ErrorText"
import {TeamAssignment, User} from "@irati/types"
import UserSearch from "@irati/ui/src/UserSearch"
import GenericModal from "@irati/ui/src/GenericModal"
import Form from "@irati/ui/src/Form"

interface Props extends InjectIntl {
  show: boolean
  teamId: string
  onHide: () => void
  onSave: (managers: number) => void
}

const AssignManagersModal: FC<Props> = ({show, teamId, onHide, onSave, formatMessage}) => {
  const [users, setUsers] = useState<User[]>([])
  const [submit, setSubmit] = useState<Promise<any> | null>(null)
  const [submitError, setSubmitError] = useState<null | string>(null)

  const handleAssign = () => {
    setSubmitError(null)
    const submitted = Promise.all(users.map((user) => teamService.addUserToTeam(teamId, user.uuid!, TeamAssignment.Manager)))
      .then(() => {
        onSave(users.length)
      })
      .catch((error: PBError) => {
        const message = getMessage(formatMessage, getErrorMessage(error), getErrorValues(error))
        setSubmitError(message)
      })
    setSubmit(submitted)
  }

  const hangleOnChange = (_: any, selectedUsers) => {
    if (selectedUsers?.length > 0) {
      setUsers(uniqBy(users.concat(selectedUsers), "uuid"))
    } else {
      setUsers([])
    }
  }

  useEffect(() => {
    if (show) {
      setSubmitError(null)
      setUsers([])
    }
  }, [show])

  return (
    <GenericModal customWidth={600} show={show} hideActions onHide={() => onHide()}>
      <Form>
        <Flex direction="column">
          <H2 marginBottom={2}>
            <FormattedMessage id="admin.components.teamList.assignManagersModal.title" />
          </H2>
          <Body marginBottom>
            <FormattedMessage id="admin.components.teamList.assignManagersModal.desc" />
          </Body>
          <UserSearch
            id="managers-selector"
            isMulti
            hideLabel
            onChange={hangleOnChange}
            placeholder="platform.fields.selectRecipient.placeholder"
            recipients={users.map(({uuid}) => uuid)}
            error={users?.length === 0}
            validation={Validation.string(formatMessage({id: "admin.components.teamList.assignManagersModal.error"}))}
          />
        </Flex>
        <Flex justifyContent="flex-end">
          <Button load={submit} primary onClick={handleAssign} disabled={users?.length === 0}>
            <FormattedMessage id="admin.components.teamList.assignManagersModal.btn" />
          </Button>
        </Flex>
        <Flex justifyContent="flex-end" marginTop>
          {submitError && <ErrorText>{submitError}</ErrorText>}
        </Flex>
      </Form>
    </GenericModal>
  )
}

export default injectIntl(AssignManagersModal)
